import React, { useCallback, useMemo } from "react";
import { Box, Button, Divider, Link, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Info, Notifications, WhatsApp } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { CommonDialog } from "./CommonDialog";
import { usePostAttendanceContext } from "@/contexts/PostAttendanceContext";
import {
  AppointmentReminderWhatsAppContext,
  buildAppointmentReminderMessage,
  buildAppointmentReminderWhatsAppUrl,
} from "./appointmentReminderWhatsApp";

interface AppointmentReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  reminderContext: AppointmentReminderWhatsAppContext;
}

const primaryLinkSx = {
  color: "primary.main",
  fontWeight: 600,
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
} as const;

/**
 * Modal de lembrete da consulta — mesmo padrão visual do PaymentPendingModal.
 */
export function AppointmentReminderModal({
  isOpen,
  onClose,
  reminderContext,
}: AppointmentReminderModalProps) {
  const theme = useTheme();
  const { contractPostAttendance } = usePostAttendanceContext();
  const hasFluxZapExtension = !!contractPostAttendance;

  const reminderMessage = useMemo(
    () => buildAppointmentReminderMessage(reminderContext),
    [reminderContext],
  );

  const whatsAppReminderUrl = useMemo(
    () => buildAppointmentReminderWhatsAppUrl(reminderContext),
    [reminderContext],
  );

  const handleOpenWhatsAppReminder = useCallback(() => {
    window.open(whatsAppReminderUrl, "_blank", "noopener,noreferrer");
  }, [whatsAppReminderUrl]);

  const whatsAppButtonSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 600,
    borderColor: alpha(theme.palette.primary.main, 0.5),
    color: "primary.main",
    bgcolor: alpha(theme.palette.primary.main, 0.06),
    "&:hover": {
      borderColor: "primary.main",
      bgcolor: alpha(theme.palette.primary.main, 0.12),
    },
  } as const;

  return (
    <CommonDialog isOpen={isOpen} handleClose={onClose}>
      <Stack spacing={3} alignItems="center" p={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Notifications sx={{ fontSize: 40, color: "secondary.main" }} />
          <Typography
            variant="h6"
            fontWeight={500}
            sx={{ color: "neutral1000.main" }}
          >
            Enviar lembrete
          </Typography>
        </Stack>

        <Typography variant="subtitle2" textAlign="center">
          Envie um lembrete de confirmação da consulta para{" "}
          <strong>{reminderContext.patientName}</strong>.
        </Typography>

        <Box
          sx={{
            px: 1,
            py: 1.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.info.main, 0.06),
            border: "1px dashed",
            borderColor: alpha(theme.palette.info.main, 0.35),
            width: "100%",
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" textAlign="center">
            <Box component="span" fontWeight={700} color="text.primary">
              Configure o FluxZap
            </Box>{" "}
            para enviar confirmações e cancelamentos de consultas automaticamente
            por WhatsApp.{" "}
            <Link
              component={RouterLink}
              to={hasFluxZapExtension ? "/fluxzap" : "/extensions"}
              state={
                hasFluxZapExtension ? undefined : { highlightFluxZap: true }
              }
              onClick={onClose}
              sx={primaryLinkSx}
            >
              Clique aqui
            </Link>{" "}
            para {hasFluxZapExtension ? "configurar" : "ativar"}.
          </Typography>
        </Box>

        <Box
          sx={{
            px: 2,
            py: 1.75,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.grey[500], 0.06),
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.9),
            width: "100%",
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Info
              sx={{
                fontSize: 20,
                color: "secondary600.main",
                mt: 0.15,
                flexShrink: 0,
              }}
            />
            <Stack spacing={1.25} width="100%">
              <Typography
                variant="body2"
                color="text.secondary"
                lineHeight={1.6}
              >
                O botão abaixo abre o WhatsApp Web com o lembrete da consulta
                pronto para enviar a{" "}
                <Box component="span" fontWeight={600} color="text.primary">
                  {reminderContext.patientName}
                </Box>
                .
              </Typography>

              <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.8) }} />

              <Box width="100%">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  display="block"
                  mb={0.75}
                >
                  Mensagem que será enviada
                </Typography>
                <Box
                  sx={{
                    position: "relative",
                    px: 1.75,
                    py: 1.5,
                    borderRadius: 2,
                    borderTopLeftRadius: 0.5,
                    bgcolor: "primary100.main",
                    border: "1px solid",
                    borderColor: alpha(theme.palette.primary.main, 0.18),
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: 3,
                      height: "100%",
                      borderRadius: "2px 0 0 2px",
                      bgcolor: "primary.main",
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.primary"
                    lineHeight={1.65}
                    sx={{
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                      pl: 0.5,
                    }}
                  >
                    {reminderMessage}
                  </Typography>
                </Box>
              </Box>

              <Stack alignItems="center" width="100%">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<WhatsApp />}
                  onClick={handleOpenWhatsAppReminder}
                  sx={whatsAppButtonSx}
                >
                  Enviar lembrete via WhatsApp Web
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </CommonDialog>
  );
}
